import React, { useEffect, useRef, useState } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MapPolyline } from "../MapPolyline";
import { MapMarker } from "../elements/MapMarker";
import { useSetPilotageLoaded, useSetShipPositionLoaded, useSetIsAisActive, useMapBounds, useMapZoom, useMapDragEnd, useMapResize } from "../../hooks/useMapStore";
import { isArrayEmpty } from "components/helpers/ObjectHelpers";
import MapApiRepository from "../../repositories/MapApiRepository";
import MapService, { IMapService } from "../../services/MapService";
import { IconColors } from "../../../../../services/SystemNames";
import MapPropertiesService from "../../services/MapPropertiesService";
import Moment from 'moment';
import { isLocationAtSea } from "../../../../../pages/pilot/pilot-assignment/helpers/PilotAssignmentHelpers";
import { isRequestAborted } from "../../../../helpers/ApiHelpers";
import { useWarningNotification, AppNotificationSystemNames } from 'components/hooks/useAppStore';

export const MapPilotageMarkers = ({ pilotageId } : { pilotageId: number }) => {

    const addWarningNotification = useWarningNotification();

    const map = useMap();
    const setPilotageLoaded = useSetPilotageLoaded();
    const setShipPositionLoaded = useSetShipPositionLoaded();
    const setIsAisActive = useSetIsAisActive();
    const bounds = useMapBounds();
    const zoom = useMapZoom();
    const dragEnd = useMapDragEnd();
    const resize = useMapResize();
    
    const [locationMarkers, setLocationMarkers] = useState<any[]>([]);
    const [polylinePaths, setPolylinePaths] = useState<any[]>([]);
    const [shipMarker, setShipMarker] = useState<any>(null);
    const [pilotage, setPilotage] = useState<any>(null);

    const mapService: IMapService = new MapService();

    const componentRef: {
        current: {
            shipPositionPolyline: any,
        }
    } = useRef({
        shipPositionPolyline: null
    });
    const { current: localRef } = componentRef;

    const CreateLocationMarker = (location: any, color: string) => {
        return {
            description: location.name,
            position: new window.google.maps.LatLng(location.latitude, location.longitude),
            type: "pin",
            color: color,
            topmost: true
        };
    }

    const CreateLocationMarkers = (pilotage: any) => {
        const markers = [];
        const fromLocation = pilotage.fromLocation;
        const toLocation = pilotage.toLocation;

        markers.push(CreateLocationMarker(fromLocation, IconColors.PinStart));
        markers.push(CreateLocationMarker(toLocation, IconColors.PinStop));

        return markers;
    }

    const CreateLocationPaths = (pilotage: any, shipPosition: any) => {

        const mapProperties = MapPropertiesService.properties;

        let shouldShowPolyline = false;
        let startPosition = null;

        const fromTime = Moment(pilotage.pilotageDetail.fromTime);
        const estimatedToTime = Moment(pilotage.pilotageDetail.fromTime).add(pilotage.pilotageDetail.pilotageEstimatedDurationInMinutes, 'minutes');
        const isFromLocationAtSea = isLocationAtSea(pilotage.fromLocation);
        const isToLocationAtSea = isLocationAtSea(pilotage.toLocation);

        if (
            (isFromLocationAtSea && !isToLocationAtSea) ||
            (!isFromLocationAtSea && !isToLocationAtSea) ||
            (isFromLocationAtSea && isToLocationAtSea)
        ) {
            shouldShowPolyline = fromTime > Moment();
            startPosition = new window.google.maps.LatLng(pilotage.fromLocation.latitude, pilotage.fromLocation.longitude);
        } else if (isToLocationAtSea) {
            shouldShowPolyline = estimatedToTime > Moment() && shipPosition.speedOverGround > mapProperties.MinimumSpeed;
            startPosition = new window.google.maps.LatLng(pilotage.toLocation.latitude, pilotage.toLocation.longitude);
        }

        const paths: google.maps.LatLng[] = [];
        if (shouldShowPolyline) {
            paths.push(startPosition as google.maps.LatLng);
            paths.push(shipPosition);
        }

        return paths;
    }

    const fetchShipPosition = async (pilotage: any) => {

        if (localRef.shipPositionPolyline) {
            localRef.shipPositionPolyline.setMap(null);
        }

        let position = null;

        const response: any = await MapApiRepository.getShipPositionsAsync([pilotage.pilotageShip.shipId]);
        if (response.ok) {
            const shipData = await response.json();
            if (!isArrayEmpty(shipData)) {
                // create ship marker
                const marker = mapService.createShipPositionInformationObject(shipData[0], true, true);
                position = marker.position;
                setShipMarker(marker);

                // create polyline
                const paths = CreateLocationPaths(pilotage, position);
                setPolylinePaths(paths);
            }

        } else {
            OnShipPositionResponseError(response, addWarningNotification);
            setShipMarker(null);
        }
        return position;
    }

    const fetchMarkers = async () => {

        const positions = [];

        // fetch pilotage
        const response: any = await MapApiRepository.getReadOnlyPilotageByIdAsync(pilotageId);
        if (response.ok) {
            const data = await response.json();
            const markers = CreateLocationMarkers(data);

            setPilotage(data);
            setLocationMarkers(markers);

            positions.push(markers[0].position);
            positions.push(markers[1].position);

            const shipPostion = await fetchShipPosition(data);
            if (shipPostion) {
                positions.push(shipPostion);
            }
        } else {
            addWarningNotification("Kunne ikke hente losoppdraget.");
        }

        FitBounds(map, positions);
        setPilotageLoaded();
        setShipPositionLoaded();

        setTimeout(() => {
            setIsAisActive(true);
        }, 500);
    }

    

    useEffect(() => {
        if (pilotageId === 0) return;
        fetchMarkers();
    }, [pilotageId]);

    useEffect(() => {
        if (!pilotage) return;
        fetchShipPosition(pilotage);
    }, [bounds, zoom, dragEnd, resize]);

    return (
        <>
            {
                (!isArrayEmpty(polylinePaths)) &&
                <MapPolyline
                    paths={polylinePaths}
                    strokeColor="#f06450"
                />
            }
            {
                locationMarkers.map((marker: any, index: number) =>
                    <MapMarker
                        key={index}
                        marker={marker}
                    />
                )
            }
            {
                (shipMarker) &&
                <MapMarker
                    marker={shipMarker}
                />
            }
        </>
    );
}
const FitBounds = (map: any, positions: any[]) => {
    if (!map) return;

    const bounds = new window.google.maps.LatLngBounds();
    for (const position of positions) {
        bounds.extend(position);
    }
    map.fitBounds(bounds);
}

const OnShipPositionResponseError = (response: any, addWarningNotification:any) => {
    if (isRequestAborted(response)) return;
    addWarningNotification("Kunne ikke finne fart&oslash;yets AIS posisjon.", AppNotificationSystemNames.MapAisFetchShipDataError);
}
